/* eslint-disable max-lines */
import {
  activePinIdAtom,
  currentPinningSessionAtom,
  hoveredPinIdAtom,
  hoveredPinSourceAtom,
  pinningFeedbackLastEditedPinIdAtom,
  pinningMobileViewAtom,
  pinningPanelExpandedAtom,
  pinsAtom,
  useRemovePin,
  useSubmitPinMessage,
  useUpdatePinInstruction,
  type AcknowledgmentPayload,
  type PinMessage,
  type Pin as PinType,
  type ResultPayload
} from "@design-lab/entities/pinning";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import {
  trackDesignLabPinClarificationClicked,
  trackDesignLabPinDeleteFailed,
  trackDesignLabPinDeleted,
  trackDesignLabPinError,
  trackDesignLabPinMessageSubmitted,
  trackDesignLabPinNeedsClarification,
  trackDesignLabPinReady,
  trackDesignLabPinSelected,
  trackDesignLabPinUnsupported,
  type AnalyticsClarification
} from "@design-lab/shared/lib/analytics";
import { useToast } from "@design-lab/shared/ui";
import { generateUUID } from "common/utils/uuid";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { getClarificationAnswer } from "./PinClarifications";

const MIN_MESSAGE_LENGTH = 1;

export type ClarificationResponse = {
  selected: string | null;
  custom: string;
  isTypingCustom: boolean;
};

function getMessageCounts(
  messages: PinMessage[],
  pendingRole?: "user" | "ai"
): { userMessagesCount: number; aiMessagesCount: number } {
  const userMessagesCount =
    messages.filter(m => m.role === "user").length + (pendingRole === "user" ? 1 : 0);
  const aiMessagesCount =
    messages.filter(m => m.role === "ai").length + (pendingRole === "ai" ? 1 : 0);
  return { userMessagesCount, aiMessagesCount };
}

// eslint-disable-next-line max-lines-per-function
export function usePinningPanel() {
  const { isDesktop } = useResponsive();
  const currentPinningSession = useAtomValue(currentPinningSessionAtom);
  const currentArtworkId = currentPinningSession?.artworkId;
  const currentSessionId = currentPinningSession?.sessionId;

  const [pins, setPins] = useAtom(pinsAtom);
  const [activePinId, setActivePinId] = useAtom(activePinIdAtom);
  const setHoveredPinId = useSetAtom(hoveredPinIdAtom);
  const setHoveredPinSource = useSetAtom(hoveredPinSourceAtom);
  const [composerText, setComposerText] = useState("");
  const [removingPinIds, setRemovingPinIds] = useState<Set<string>>(new Set());
  const [pendingDeletePinId, setPendingDeletePinId] = useState<string | null>(null);
  const [clarificationResponses, setClarificationResponses] = useState<
    Record<string, ClarificationResponse>
  >({});
  const [mobileView, setMobileView] = useAtom(pinningMobileViewAtom);
  const [isPanelExpanded, setIsPanelExpanded] = useAtom(pinningPanelExpandedAtom);
  const [editingPinId, setEditingPinId] = useState<string | null>(null);
  const setLastEditedPinId = useSetAtom(pinningFeedbackLastEditedPinIdAtom);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const pendingCursorPositionRef = useRef<number | null>(null);
  const clarificationScrollRef = useRef<HTMLDivElement>(null);
  const messagesScrollRef = useRef<HTMLDivElement>(null);
  const pinsContainerRef = useRef<HTMLDivElement>(null);
  const savedClarificationResponsesRef = useRef<Record<string, ClarificationResponse> | null>(null);
  const savedPinStateRef = useRef<PinType["state"] | null>(null);
  const savedMessagesRef = useRef<PinMessage[] | null>(null);
  const savedComposerTextRef = useRef<string | null>(null);
  const [unansweredAbove, setUnansweredAbove] = useState(0);
  const [unansweredBelow, setUnansweredBelow] = useState(0);

  const { mutate: removePinApi } = useRemovePin();
  const { mutate: updatePinInstructionApi, isPending: isUpdatingPinInstruction } =
    useUpdatePinInstruction();
  const { error: showErrorToast } = useToast();

  const activePin = useMemo(
    () => pins.find(p => p.id === activePinId) ?? null,
    [activePinId, pins]
  );

  // Reset expanded state when going to list view or when active pin becomes ready/unsupported
  useEffect(() => {
    if (mobileView === "list") {
      setIsPanelExpanded(false);
    }
  }, [mobileView, setIsPanelExpanded]);

  // Auto-scroll messages to bottom when messages change
  useEffect(() => {
    if (messagesScrollRef.current && activePin?.messages.length) {
      messagesScrollRef.current.scrollTop = messagesScrollRef.current.scrollHeight;
    }
  }, [activePin?.messages.length, activePin?.state]);

  useEffect(() => {
    if (activePin?.state === "ready" || activePin?.state === "unsupported") {
      setIsPanelExpanded(false);
    }
  }, [activePin?.state, setIsPanelExpanded]);

  const clearRemovingPin = useCallback((pinId: string) => {
    setRemovingPinIds(prev => {
      const next = new Set(prev);
      next.delete(pinId);
      return next;
    });
  }, []);

  // Clear pins when unmounting
  useEffect(
    () => () => {
      setPins([]);
      setActivePinId(null);
      setLastEditedPinId(null);
    },
    [setPins, setActivePinId, setLastEditedPinId]
  );

  // Reset composer when active pin changes
  useEffect(() => {
    setComposerText("");
    setClarificationResponses({});
  }, [activePinId]);

  // Auto-focus composer
  useEffect(() => {
    const shouldFocus =
      (activePin?.state === "draft" && activePin.messages.length === 0) ||
      activePin?.state === "needs_clarification";

    if (shouldFocus && textareaRef.current) {
      setTimeout(() => {
        textareaRef.current?.focus();
        textareaRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 100);
    }
  }, [activePin?.state, activePin?.messages.length, activePinId]);

  // Handle cursor positioning after newline
  useLayoutEffect(() => {
    if (pendingCursorPositionRef.current !== null && textareaRef.current) {
      const pos = pendingCursorPositionRef.current;
      textareaRef.current.selectionStart = pos;
      textareaRef.current.selectionEnd = pos;
      textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
      pendingCursorPositionRef.current = null;
    }
  }, [composerText]);

  const createUuid = useCallback(() => generateUUID(), []);

  const updatePin = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (pinId: string, updater: (pin: PinType) => any) => {
      setPins(prev => prev.map(pin => (pin.id === pinId ? updater(pin) : pin)));
    },
    [setPins]
  );

  const deletePin = useCallback(
    (pinId: string) => {
      setPins(prev => prev.filter(p => p.id !== pinId));
      if (activePinId === pinId) {
        setActivePinId(null);
      }
    },
    [activePinId, setActivePinId, setPins]
  );

  const handleDeletePin = useCallback(
    (pinId: string) => {
      const sessionId = currentSessionId;

      if (!sessionId) {
        deletePin(pinId);
        return;
      }

      const pin = pins.find(p => p.id === pinId);
      const hasBeenSyncedToBackend = (pin?.messages?.length ?? 0) > 0 || pin?.state !== "draft";

      if (!hasBeenSyncedToBackend) {
        if (currentArtworkId && currentSessionId) {
          trackDesignLabPinDeleted({
            artworkId: currentArtworkId,
            sessionId: currentSessionId,
            pinId,
            pinState: pin?.state ?? "draft",
            hadMessages: false
          });
        }
        deletePin(pinId);
        if (!isDesktop) {
          setMobileView("list");
        }
        return;
      }

      if (removingPinIds.has(pinId)) {
        return;
      }

      setRemovingPinIds(prev => new Set(prev).add(pinId));

      removePinApi(
        { sessionId, pinId },
        {
          onSuccess: () => {
            if (currentArtworkId && currentSessionId) {
              trackDesignLabPinDeleted({
                artworkId: currentArtworkId,
                sessionId: currentSessionId,
                pinId,
                pinState: pin?.state ?? "draft",
                hadMessages: (pin?.messages?.length ?? 0) > 0
              });
            }
            deletePin(pinId);
            clearRemovingPin(pinId);
            if (!isDesktop) {
              setMobileView("list");
            }
          },
          onError: () => {
            if (currentArtworkId && currentSessionId) {
              trackDesignLabPinDeleteFailed({
                artworkId: currentArtworkId,
                sessionId: currentSessionId,
                pinId
              });
            }
            clearRemovingPin(pinId);
            showErrorToast({
              title: "Failed to remove pin",
              description: "Something went wrong. Please try again."
            });
          }
        }
      );
    },
    [
      currentArtworkId,
      currentSessionId,
      removePinApi,
      deletePin,
      pins,
      removingPinIds,
      clearRemovingPin,
      showErrorToast,
      isDesktop,
      setMobileView
    ]
  );

  const openDeletePinModal = useCallback((pinId: string) => {
    setPendingDeletePinId(pinId);
  }, []);

  const closeDeletePinModal = useCallback(() => {
    setPendingDeletePinId(null);
  }, []);

  const confirmDeletePin = useCallback(() => {
    if (!pendingDeletePinId) return;
    handleDeletePin(pendingDeletePinId);
    setPendingDeletePinId(null);
  }, [handleDeletePin, pendingDeletePinId]);

  const openEditPinModal = useCallback((pinId: string) => {
    setEditingPinId(pinId);
  }, []);

  const closeEditPinModal = useCallback(() => {
    setEditingPinId(null);
  }, []);

  const editingPin = useMemo(
    () => pins.find(p => p.id === editingPinId) ?? null,
    [editingPinId, pins]
  );

  const handleUpdatePinInstruction = useCallback(
    (instruction: string) => {
      if (!editingPinId || !currentSessionId) return;

      updatePinInstructionApi(
        { sessionId: currentSessionId, pinId: editingPinId, instruction },
        {
          onSuccess: () => {
            setPins(prev =>
              prev.map(pin => (pin.id === editingPinId ? { ...pin, instruction } : pin))
            );
            setEditingPinId(null);
          },
          onError: () => {
            showErrorToast({
              title: "Failed to update pin",
              description: "Something went wrong. Please try again."
            });
          }
        }
      );
    },
    [editingPinId, currentSessionId, updatePinInstructionApi, setPins, showErrorToast]
  );

  const { mutate: submitMessage } = useSubmitPinMessage({
    onAcknowledgment: (payload: AcknowledgmentPayload) => {
      if (!activePinId) return;
      updatePin(activePinId, pin => ({
        ...pin,
        thinkingText: payload.message || undefined
      }));
    },
    onResult: (payload: ResultPayload) => {
      if (!activePinId) return;

      const nextState =
        payload.status === "ready" ||
        payload.status === "needs_clarification" ||
        payload.status === "unsupported"
          ? payload.status
          : "ready";

      const clarifications = payload.clarifications ?? [];
      const resultMessage = payload.message || "Done.";

      const currentPin = pins.find(p => p.id === activePinId);
      const { userMessagesCount, aiMessagesCount } = getMessageCounts(
        currentPin?.messages ?? [],
        "ai"
      );

      const askingClarifications = clarifications.filter(
        c => c.status === "asking" && c.options.length > 0
      );
      const hasClarifications =
        nextState === "needs_clarification" && askingClarifications.length > 0;

      updatePin(activePinId, pin => {
        const areasOfInterestUpdate = payload.aoiResult
          ? {
              areasOfInterest: payload.aoiResult,
              highlightAreasEnabled: pin.highlightAreasEnabled ?? true
            }
          : {};

        return {
          ...pin,
          state: nextState,
          instruction: payload.instruction,
          thinkingText: undefined,
          ...areasOfInterestUpdate,
          messages: [
            ...pin.messages,
            {
              id: createUuid(),
              role: "ai",
              content: resultMessage,
              timestamp: Date.now(),
              ...(hasClarifications && { clarifications, textInfo: payload.text })
            }
          ]
        };
      });

      if (hasClarifications) {
        setClarificationResponses({});
      }

      // Clear saved refs on success - no longer needed for error recovery
      savedClarificationResponsesRef.current = null;
      savedPinStateRef.current = null;
      savedMessagesRef.current = null;
      savedComposerTextRef.current = null;

      if (currentArtworkId && currentSessionId) {
        const trackingData = {
          artworkId: currentArtworkId,
          sessionId: currentSessionId,
          pinId: activePinId,
          userMessagesCount,
          aiMessagesCount
        };

        if (nextState === "ready") {
          trackDesignLabPinReady(trackingData);
        } else if (nextState === "unsupported") {
          trackDesignLabPinUnsupported(trackingData);
        } else if (nextState === "needs_clarification") {
          trackDesignLabPinNeedsClarification({
            ...trackingData,
            optionsCount: askingClarifications.reduce((sum, c) => sum + c.options.length, 0),
            questionType: askingClarifications.map(c => c.type).join(",")
          });
        }
      }
    },
    onError: () => {
      if (!activePinId) return;

      const currentPin = pins.find(p => p.id === activePinId);
      const { userMessagesCount, aiMessagesCount } = getMessageCounts(
        currentPin?.messages ?? [],
        "ai"
      );

      if (currentArtworkId && currentSessionId) {
        trackDesignLabPinError({
          artworkId: currentArtworkId,
          sessionId: currentSessionId,
          pinId: activePinId,
          errorType: "processing_error",
          userMessagesCount,
          aiMessagesCount
        });
      }

      // Show error toast instead of inline message
      showErrorToast({
        title: "Something went wrong",
        description: "Please try again."
      });

      // Reset chat state: restore previous messages (including clarifications) and state
      const previousState = savedPinStateRef.current ?? "draft";
      const previousMessages = savedMessagesRef.current;
      updatePin(activePinId, pin => ({
        ...pin,
        state: previousState,
        thinkingText: undefined,
        messages: previousMessages ?? pin.messages.slice(0, -1)
      }));

      // Restore clarification responses if they were saved
      if (savedClarificationResponsesRef.current) {
        setClarificationResponses(savedClarificationResponsesRef.current);
        savedClarificationResponsesRef.current = null;
      }

      // Restore composer text if it was saved
      if (savedComposerTextRef.current !== null) {
        setComposerText(savedComposerTextRef.current);
        savedComposerTextRef.current = null;
      }

      savedPinStateRef.current = null;
      savedMessagesRef.current = null;
    }
  });

  const isComposerTextValid = useCallback(
    (text: string) => text.trim().length >= MIN_MESSAGE_LENGTH,
    []
  );

  const lastAiMessageWithClarifications = useMemo(() => {
    if (!activePin) return null;
    for (let i = activePin.messages.length - 1; i >= 0; i -= 1) {
      const msg = activePin.messages[i];
      if (msg?.role === "ai" && msg.clarifications && msg.clarifications.length > 0) return msg;
    }
    return null;
  }, [activePin]);

  const pendingPillClarifications = useMemo(() => {
    const clarifications = lastAiMessageWithClarifications?.clarifications ?? [];
    return clarifications.filter(c => c.status === "asking" && c.options.length > 0);
  }, [lastAiMessageWithClarifications]);

  const fontClarification = useMemo(
    () => pendingPillClarifications.find(c => c.type === "font_selection"),
    [pendingPillClarifications]
  );

  const colorClarification = useMemo(
    () => pendingPillClarifications.find(c => c.type === "font_color"),
    [pendingPillClarifications]
  );

  const selectedFontValue = useMemo(() => {
    if (!fontClarification) return undefined;
    const response = clarificationResponses[fontClarification.question];
    if (response?.isTypingCustom) return response.custom || undefined;
    if (response?.selected) {
      const opt = fontClarification.options.find(o => o.label === response.selected);
      return opt?.value || response.selected;
    }
    return undefined;
  }, [fontClarification, clarificationResponses]);

  const selectedColorValue = useMemo(() => {
    if (!colorClarification) return undefined;
    const response = clarificationResponses[colorClarification.question];
    if (response?.isTypingCustom) return response.custom || undefined;
    if (response?.selected) {
      const opt = colorClarification.options.find(
        o => `${o.label} (${o.value})` === response.selected
      );
      return opt?.value || response.selected;
    }
    return undefined;
  }, [colorClarification, clarificationResponses]);

  const calculateUnansweredPositions = useCallback(() => {
    const scrollContainer = clarificationScrollRef.current;
    if (!scrollContainer) return;

    const containerRect = scrollContainer.getBoundingClientRect();
    let above = 0;
    let below = 0;

    pendingPillClarifications.forEach(clarification => {
      const isUnanswered = !getClarificationAnswer(clarificationResponses[clarification.question]);

      if (isUnanswered) {
        const labelValue = clarification.label || clarification.question;
        const element = scrollContainer.querySelector(
          `[data-clarification-label="${CSS.escape(labelValue)}"]`
        );
        if (element) {
          const elementRect = element.getBoundingClientRect();
          if (elementRect.bottom < containerRect.top) {
            above += 1;
          } else if (elementRect.top > containerRect.bottom) {
            below += 1;
          }
        }
      }
    });

    setUnansweredAbove(above);
    setUnansweredBelow(below);
  }, [pendingPillClarifications, clarificationResponses]);

  const scrollToFirstUnanswered = useCallback(
    (direction: "above" | "below") => {
      const scrollContainer = clarificationScrollRef.current;
      if (!scrollContainer) return;

      const containerRect = scrollContainer.getBoundingClientRect();

      for (const clarification of pendingPillClarifications) {
        const isUnanswered = !getClarificationAnswer(
          clarificationResponses[clarification.question]
        );

        if (isUnanswered) {
          const labelValue = clarification.label || clarification.question;
          const element = scrollContainer.querySelector(
            `[data-clarification-label="${CSS.escape(labelValue)}"]`
          );

          if (element) {
            const elementRect = element.getBoundingClientRect();
            const isTargetDirection =
              direction === "above"
                ? elementRect.bottom < containerRect.top
                : elementRect.top > containerRect.bottom;

            if (isTargetDirection) {
              element.scrollIntoView({ behavior: "smooth", block: "start" });
              break;
            }
          }
        }
      }
    },
    [pendingPillClarifications, clarificationResponses]
  );

  useEffect(() => {
    if (pendingPillClarifications.length > 0) {
      calculateUnansweredPositions();
    }
  }, [pendingPillClarifications.length, clarificationResponses, calculateUnansweredPositions]);

  const handleSelectClarificationOption = useCallback(
    (question: string, value: string | null) => {
      setClarificationResponses(prev => {
        const wasUnanswered = !getClarificationAnswer(prev[question]);

        const newResponses = {
          ...prev,
          [question]: {
            selected: value,
            custom: prev[question]?.custom ?? "",
            isTypingCustom: false
          }
        };

        if (currentArtworkId && currentSessionId && activePinId && activePin) {
          trackDesignLabPinClarificationClicked({
            artworkId: currentArtworkId,
            sessionId: currentSessionId,
            pinId: activePinId,
            optionValue: value || "",
            turnIndex: activePin.messages.filter(m => m.role === "user").length + 1,
            userMessagesCount: activePin.messages.filter(m => m.role === "user").length,
            aiMessagesCount: activePin.messages.filter(m => m.role === "ai").length,
            optionType: "suggested"
          });
        }

        if (value !== null && wasUnanswered) {
          setTimeout(() => {
            const currentIndex = pendingPillClarifications.findIndex(c => c.question === question);

            const reorderedClarifications = [
              ...pendingPillClarifications.slice(currentIndex + 1),
              ...pendingPillClarifications.slice(0, currentIndex)
            ];
            const nextUnanswered = reorderedClarifications.find(
              c => !getClarificationAnswer(newResponses[c.question])
            );

            if (nextUnanswered) {
              const scrollContainer = clarificationScrollRef.current;
              const labelValue = nextUnanswered.label || nextUnanswered.question;
              const targetElement = scrollContainer?.querySelector(
                `[data-clarification-label="${CSS.escape(labelValue)}"]`
              );
              if (targetElement && scrollContainer) {
                targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
              }
            }
          }, 100);
        }

        return newResponses;
      });
    },
    [pendingPillClarifications, currentArtworkId, currentSessionId, activePinId, activePin]
  );

  const handleToggleCustomInput = useCallback(
    (question: string) => {
      setClarificationResponses(prev => ({
        ...prev,
        [question]: {
          selected: prev[question]?.selected ?? null,
          custom: prev[question]?.custom ?? "",
          isTypingCustom: !prev[question]?.isTypingCustom
        }
      }));

      if (currentArtworkId && currentSessionId && activePinId && activePin) {
        trackDesignLabPinClarificationClicked({
          artworkId: currentArtworkId,
          sessionId: currentSessionId,
          pinId: activePinId,
          optionValue: "",
          turnIndex: activePin.messages.filter(m => m.role === "user").length + 1,
          userMessagesCount: activePin.messages.filter(m => m.role === "user").length,
          aiMessagesCount: activePin.messages.filter(m => m.role === "ai").length,
          optionType: "custom"
        });
      }
    },
    [currentArtworkId, currentSessionId, activePinId, activePin]
  );

  const handleCustomInputChange = useCallback((question: string, value: string) => {
    setClarificationResponses(prev => ({
      ...prev,
      [question]: {
        selected: prev[question]?.selected ?? null,
        custom: value,
        isTypingCustom: true
      }
    }));
  }, []);

  const formatClarificationResponses = useCallback(() => {
    const parts: string[] = [];
    const analyticsClarifications: AnalyticsClarification[] = [];

    for (const clarification of pendingPillClarifications) {
      const value = getClarificationAnswer(clarificationResponses[clarification.question]);
      const optionType = clarificationResponses[clarification.question]?.isTypingCustom
        ? "custom"
        : "suggested";
      analyticsClarifications.push({
        question: clarification.question,
        value: value || "",
        option_type: optionType
      });
      if (value) {
        const labelText = clarification.label || clarification.question;
        parts.push(`"${labelText}": ${value}`);
      }
    }

    return { clarificationText: parts.join("\n"), clarifications: analyticsClarifications };
  }, [pendingPillClarifications, clarificationResponses]);

  const hasAnyClarificationResponse = useMemo(
    () =>
      pendingPillClarifications.some(c =>
        getClarificationAnswer(clarificationResponses[c.question])
      ),
    [pendingPillClarifications, clarificationResponses]
  );

  const handleSubmit = useCallback(() => {
    if (!activePin) return;
    if (activePin.state === "processing") return;
    const sessionId = currentSessionId ?? null;

    const { clarificationText, clarifications } = formatClarificationResponses();
    const messageText = composerText.trim();
    const fullMessage = [clarificationText, messageText].filter(Boolean).join("\n");

    if (!fullMessage) return;

    const pinId = activePin.id;
    const now = Date.now();
    const userMessageId = createUuid();
    const isFirstMessage = activePin.messages.filter(m => m.role === "user").length === 0;
    const turnIndex = activePin.messages.filter(m => m.role === "user").length + 1;

    // Save state before submission for error recovery
    savedClarificationResponsesRef.current = { ...clarificationResponses };
    savedPinStateRef.current = activePin.state;
    savedMessagesRef.current = [...activePin.messages];
    savedComposerTextRef.current = composerText;

    updatePin(pinId, pin => ({
      ...pin,
      state: "processing" as const,
      thinkingText: undefined,
      messages: [
        ...pin.messages.map(m => ({ ...m, clarifications: undefined })),
        { id: userMessageId, role: "user" as const, content: fullMessage, timestamp: now }
      ]
    }));
    setComposerText("");
    setClarificationResponses({});
    setLastEditedPinId(pinId);

    const { userMessagesCount, aiMessagesCount } = getMessageCounts(activePin.messages, "user");

    if (currentArtworkId && currentSessionId) {
      trackDesignLabPinMessageSubmitted({
        artworkId: currentArtworkId,
        sessionId: currentSessionId,
        pinId,
        isFirstMessage,
        turnIndex,
        userMessagesCount,
        aiMessagesCount,
        clarifications
      });
    }

    if (!sessionId) {
      return;
    }

    const priorPins = pins
      .filter(p => p.id !== pinId)
      .map(p => ({
        pin_id: p.id,
        coordinates: p.coordinates,
        state: p.state,
        instruction: p.instruction ?? ""
      }));

    submitMessage({
      sessionId,
      pinId,
      coordinates: activePin.coordinates,
      userMessage: fullMessage,
      isFirstMessage,
      priorPins
    });
  }, [
    activePin,
    composerText,
    createUuid,
    currentArtworkId,
    currentSessionId,
    formatClarificationResponses,
    pins,
    setLastEditedPinId,
    submitMessage,
    updatePin
  ]);

  // Mobile-specific effects
  useEffect(() => {
    if (!isDesktop && activePinId) {
      const pin = pins.find(p => p.id === activePinId);
      if (pin?.isNew && pin?.state === "draft" && pin?.messages.length === 0) {
        setMobileView("detail");
      }
    }
  }, [isDesktop, activePinId, pins]);

  // Auto-select incomplete pin
  useEffect(() => {
    if (pins.length === 0) return;

    const incompletePin = pins.find(p => p.state !== "ready" && p.state !== "unsupported");
    if (!incompletePin) return;

    if (isDesktop) {
      if (!activePinId) {
        setActivePinId(incompletePin.id);
        // Scroll to the pin after DOM updates
        setTimeout(() => {
          const pinElement = pinsContainerRef.current?.querySelector(
            `[data-pin-id="${incompletePin.id}"]`
          );
          pinElement?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
      }
    } else if (mobileView === "list") {
      setActivePinId(incompletePin.id);
      setMobileView("detail");
    }
  }, [isDesktop, pins, mobileView, activePinId, setActivePinId, setMobileView]);

  useEffect(() => {
    if (!isDesktop && pins.length === 0) {
      setMobileView("list");
    }
  }, [isDesktop, pins.length]);

  useEffect(() => {
    if (!isDesktop && mobileView === "detail" && activePin) {
      const isCompleted = activePin.state === "ready" || activePin.state === "unsupported";
      if (isCompleted) {
        setHoveredPinId(activePin.id);
        setHoveredPinSource("panel");
      }
    }
    return () => {
      if (!isDesktop) {
        setHoveredPinId(null);
        setHoveredPinSource(null);
      }
    };
  }, [isDesktop, mobileView, activePin, setHoveredPinId, setHoveredPinSource]);

  const trackPinSelected = useCallback(
    (pinId: string, previousPinId: string | null, pinState: string) => {
      if (currentArtworkId && currentSessionId) {
        trackDesignLabPinSelected({
          artworkId: currentArtworkId,
          sessionId: currentSessionId,
          pinId,
          previousPinId,
          pinState
        });
      }
    },
    [currentArtworkId, currentSessionId]
  );

  return {
    // State
    isDesktop,
    pins,
    activePinId,
    activePin,
    composerText,
    clarificationResponses,
    removingPinIds,
    pendingDeletePinId,
    editingPinId,
    editingPin,
    isUpdatingPinInstruction,
    mobileView,
    isPanelExpanded,
    unansweredAbove,
    unansweredBelow,

    // Refs
    textareaRef,
    pendingCursorPositionRef,
    clarificationScrollRef,
    messagesScrollRef,
    pinsContainerRef,

    // Derived/Memos
    pendingPillClarifications,
    selectedFontValue,
    selectedColorValue,
    hasAnyClarificationResponse,
    lastAiMessageWithClarifications,

    // Setters
    setActivePinId,
    setComposerText,
    setMobileView,
    setIsPanelExpanded,
    setHoveredPinId,
    setHoveredPinSource,

    // Handlers
    handleDeletePin,
    openDeletePinModal,
    closeDeletePinModal,
    confirmDeletePin,
    openEditPinModal,
    closeEditPinModal,
    handleUpdatePinInstruction,
    handleSelectClarificationOption,
    handleToggleCustomInput,
    handleCustomInputChange,
    handleSubmit,
    isComposerTextValid,
    calculateUnansweredPositions,
    scrollToFirstUnanswered,
    trackPinSelected
  };
}
